import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useStudio } from "./StudioContext";
import { StudioPage, Panel, Drawer, DataTable, Loading, Badge, DBlock, DKV, cx } from "./StudioShared";

const PRIO = { high: "red", medium: "amber", low: "slate" };

const titleCase = (s) => (s || "").replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Knowledge() {
  const { knowledge } = useStudio();
  const [params, setParams] = useSearchParams();
  const [domain, setDomain] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const focus = params.get("focus");
  useEffect(() => { if (focus) setOpenId(focus); }, [focus]);

  if (!knowledge) return <StudioPage title="Knowledge Explorer" question="What do they know?"><Loading /></StudioPage>;
  const entries = knowledge.entries || [];
  const domains = knowledge.domains || [];

  const rows = entries.filter((e) => {
    if (domain && e.domain !== domain) return false;
    if (q && !((e.title + e.summary + (e.tags || []).join(" ")).toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const current = entries.find((e) => e.id === openId);
  const close = () => { setOpenId(null); if (params.get("focus")) { params.delete("focus"); setParams(params, { replace: true }); } };
  const sel = "bg-navy-800 border border-white/10 text-white text-sm px-3 py-2 rounded-sm focus:border-gold focus:outline-none";

  return (
    <StudioPage title="Knowledge Explorer" question="What do they know?" testid="knowledge-explorer">
      <div className="flex flex-wrap gap-2 mb-4" data-testid="knowledge-domain-chips">
        <button onClick={() => setDomain("")} className={cx("text-xs px-2.5 py-1 rounded-sm border", !domain ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400 hover:text-white")}>All ({entries.length})</button>
        {domains.map((d) => (
          <button key={d.domain} onClick={() => setDomain(d.domain)} data-testid={`domain-chip-${d.domain}`}
            className={cx("text-xs px-2.5 py-1 rounded-sm border", domain === d.domain ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400 hover:text-white")}>{d.domain} ({d.count})</button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search knowledge…" className={cx(sel, "ml-auto w-52")} data-testid="knowledge-search" />
      </div>

      <p className="text-slate-500 text-xs mb-4" data-testid="knowledge-hint">Click any rule to see the full guidance and which strategists apply it.</p>

      <DataTable testid="knowledge-table" rows={rows} onRowClick={(e) => setOpenId(e.id)} rowTestid={(e) => `knowledge-row-${e.id}`}
        empty="No knowledge entries match."
        columns={[
          { key: "title", label: "Title", render: (e) => <span className="text-white">{e.title}</span> },
          { key: "domain", label: "Domain", render: (e) => <span className="text-slate-400">{e.domain}</span> },
          { key: "industry", label: "Industry", render: (e) => <span className="text-slate-500 text-xs">{e.industry}</span> },
          { key: "prio", label: "Priority", render: (e) => <Badge tone={PRIO[e.priority] || "slate"}>{e.priority}</Badge> },
          { key: "used", label: "Used", className: "font-mono text-xs text-center", render: (e) => `${e.usage_count || 0}×` },
          { key: "view", label: "", className: "text-right", render: () => <span className="text-gold/70 text-xs whitespace-nowrap">View →</span> },
        ]} />

      <Drawer open={!!current} onClose={close} testid="knowledge-drawer" title={current?.title} subtitle={current ? `${current.domain} · ${current.industry}` : ""}>
        {current && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone={PRIO[current.priority] || "slate"}>{current.priority} priority</Badge>
              <Badge tone="slate">confidence {Math.round((current.confidence || 0) * 100)}%</Badge>
              <Badge tone="green">{current.status}</Badge>
            </div>
            {current.summary && <DBlock title="What this rule means"><p className="text-slate-300 text-sm leading-relaxed">{current.summary}</p></DBlock>}
            {current.best_practice && <DBlock title="Best practice AIPOS applies"><p className="text-slate-300 text-sm leading-relaxed">{current.best_practice}</p></DBlock>}
            {current.supporting_notes && <DBlock title="Supporting notes"><p className="text-slate-400 text-sm leading-relaxed">{current.supporting_notes}</p></DBlock>}
            <DBlock title={`Strategists that use this${(current.related_strategists || []).length ? ` (${current.related_strategists.length})` : ""}`}>
              {(current.related_strategists || []).length > 0 ? (
                <div className="flex flex-wrap gap-1.5" data-testid="knowledge-related-strategists">
                  {current.related_strategists.map((s) => (
                    <span key={s} className="text-[11px] text-gold bg-gold/10 border border-gold/25 px-2 py-0.5 rounded-sm">{titleCase(s)}</span>
                  ))}
                </div>
              ) : <p className="text-slate-500 text-xs">Applied cross-team — no single strategist owns this rule.</p>}
            </DBlock>
            <DBlock title="How AIPOS uses this">
              <p className="text-slate-400 text-sm leading-relaxed">
                When a matching project or content request comes in, AIPOS pulls this rule into the strategists' reasoning so the proposal, website content, or plan follows it automatically.
                {typeof current.usage_count === "number" && <> It has been applied <span className="text-white font-medium">{current.usage_count}×</span> in generations so far{current.last_used ? `, most recently ${new Date(current.last_used).toLocaleDateString()}` : ""}.</>}
              </p>
            </DBlock>
            {current.tags?.length > 0 && <div className="flex flex-wrap gap-1.5">{current.tags.map((t) => <span key={t} className="text-[10px] text-slate-400 bg-white/5 px-2 py-0.5 rounded-sm">{t}</span>)}</div>}
            <div className="space-y-1.5 border-t border-white/5 pt-3">
              <DKV label="Source" value={current.source} />
              <DKV label="Version" value={current.version} />
              <DKV label="Last used" value={current.last_used ? new Date(current.last_used).toLocaleString() : "never"} />
            </div>
          </>
        )}
      </Drawer>
    </StudioPage>
  );
}
